import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import {
  EmissionReportStatus,
  CarbonTaxPaidRaw,
  EndProductEmittingMethodInput,
  ElectricityProductionMethodConfig,
  MethodEmissionReport
} from '@/api/EmissionReportModel'

const REPORTS_QUERY_KEY = 'reports'

interface CreateReportPayload {
  productionRouteId: string
  startDate: string
  endDate: string
}

export interface Report {
  id: string
  name: string
  carbonTaxPaidRaw: CarbonTaxPaidRaw
  endProductEmittingMethodInputs: EndProductEmittingMethodInput[]
  startDate: string
  endDate: string
  status: EmissionReportStatus
  submittedAt: string | null
  deletedAt: string | null
  electricityProductionConfig: Record<string, ElectricityProductionMethodConfig>
  createdAt: string
  updatedAt: string
  productionRouteId: string
  installationId: string
  finalizedReportId: string | null
  methodEmissionReports: MethodEmissionReport[]
}

export const useGetReportsForInstallation = (installationId: string) => {
  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, installationId],
    queryFn: async () => {
      const res = await apiClient.get<Report[]>(`/v2/installations/${installationId}/reports`)
      return res.data
    }
  })
}

export const useDeleteReport = (installationId: string, reportId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [REPORTS_QUERY_KEY, installationId, reportId],
    mutationFn: async () => {
      const res = await apiClient.delete<Report>(
        `/v2/installations/${installationId}/reports/${reportId}`
      )
      return res.data
    },
    onSuccess: updatedReport => {
      queryClient.setQueryData(
        [REPORTS_QUERY_KEY, installationId],
        (oldReports: Report[] | undefined) => {
          if (!oldReports) {return oldReports}
          return oldReports.map(report => (report.id === updatedReport.id ? updatedReport : report))
        }
      )
    }
  })
}

export const useCreateReport = (installationId: string) => {
  return useMutation({
    mutationKey: [REPORTS_QUERY_KEY, installationId],
    mutationFn: async (payload: CreateReportPayload) => {
      const url = `/v2/installations/${installationId}/reports`
      const res = await apiClient.post<Report>(url, payload)
      return res.data
    }
  })
}
